import api from "src/services/api";

const BASE = "solicitud-capacitacion";

export interface OpcionCatalogo {
  id: number;
  nombre: string;
  /** Solo temas: categoría de AE_C_Temas, para buscar por ella. */
  categoria?: string;
}

export interface CatalogoSolicitud {
  temas: OpcionCatalogo[];
  documentos: OpcionCatalogo[];
  dirigido_a: { codigo: string; etiqueta: string }[];
}

export interface PuntoServicio {
  id: number;
  ps_nombre: string;
  ps_codigo: string;
}

export interface OperarioBusqueda {
  legajo: string;
  nombre: string;
  cedula?: string;
  cargo?: string;
}

export type EstadoSolicitud =
  | "PENDIENTE"
  | "PROPUESTA"
  | "PROGRAMADA"
  | "PENDIENTE_CIERRE"
  | "CERRADA"
  | "RECHAZADA"
  | "CANCELADA";

export interface SolicitudCapacitacion {
  solicitud_id: number;
  estado: EstadoSolicitud;
  dirigido_a: string;
  tipo: "INDIVIDUAL" | "PS";
  /** Personas a capacitar (solo individual). */
  personas?: OperarioBusqueda[];
  ps_id: number;
  ps_codigo?: string | null;
  ps_nombre: string;
  autorizado_cliente: boolean;
  fecha_sugerida?: string | null;
  hora_sugerida?: string | null;
  comentarios: string;
  solicitante_cedula: string;
  solicitante_nombre?: string | null;
  creado_en?: string | null;
  fecha_programada?: string | null;
  hora_programada?: string | null;
  capacitador_cedula?: string | null;
  capacitador_nombre?: string | null;
  comentario_coordinador?: string | null;
  motivo_rechazo?: string | null;
  comentario_solicitante?: string | null;
  realizada?: boolean | null;
  fecha_realizada?: string | null;
  motivo_no_realizada?: string | null;
  comentarios_cierre?: string | null;
  cerrado_en?: string | null;
  items?: {
    REQUERIDA: OpcionCatalogo[];
    DOCUMENTO: OpcionCatalogo[];
    DEBILIDAD: OpcionCatalogo[];
  };
  temas_impartidos?: OpcionCatalogo[];
  capacitados?: { legajo: string; cedula?: string; nombre?: string }[];
  /** Solo en "asignadas": ya pasó un día de la fecha programada. */
  puede_cerrar?: boolean;
}

export interface NuevaSolicitudPayload {
  dirigido_a: string;
  tipo: "INDIVIDUAL" | "PS";
  /** Legajos de operarios/supervisores (solo individual). */
  personas: number[];
  ps_id: number;
  autorizado_cliente: boolean;
  fecha_sugerida?: string | null;
  hora_sugerida: string;
  capacitaciones: number[];
  documentos: number[];
  comentarios: string;
}

export interface TemaApp {
  Id: number;
  Categoria: string;
  Tema: string;
}

export interface DatosCierre {
  temas: TemaApp[];
  temas_sugeridos: number[];
  /** A quiénes se registra: las personas de la solicitud (individual) o
   *  todos los asignados activos del PS. */
  operarios_ps: OperarioBusqueda[];
}

export interface CierrePayload {
  realizada: boolean;
  motivo_no_realizada?: string;
  fecha_realizada?: string;
  temas?: number[];
  capacitados?: number[];
  /** Legajos que participaron sin figurar asignados en el sistema. */
  adicionales?: number[];
  elementos_basicos?: string;
  supervisor_nombre?: string;
  supervisor_habilidades?: string;
  supervisor_preocupacion?: string;
  colaboradores_receptivos?: string;
  debilidades?: number[];
  calidad_servicio?: string;
  usan_guantes?: boolean;
  comentarios_cierre?: string;
}

const SolicitudCapacitacionAPI = {
  rol: () => api.get<{ datos: { solicitante: boolean; capacitador: boolean } }>(`${BASE}/rol`),
  catalogo: () => api.get<{ datos: CatalogoSolicitud }>(`${BASE}/catalogo`),
  puntosServicio: (q: string) =>
    api.get<{ datos: PuntoServicio[] }>(`${BASE}/puntos-servicio?q=${encodeURIComponent(q)}`),
  operarios: (q: string) =>
    api.get<{ datos: OperarioBusqueda[] }>(`${BASE}/operarios?q=${encodeURIComponent(q)}`),
  crear: (payload: NuevaSolicitudPayload) =>
    api.post<{ info: string; id: number }>(`${BASE}/`, payload),
  misSolicitudes: () => api.get<{ datos: SolicitudCapacitacion[] }>(`${BASE}/mis-solicitudes`),
  asignadas: () => api.get<{ datos: SolicitudCapacitacion[] }>(`${BASE}/asignadas`),
  obtener: (id: number) => api.get<{ datos: SolicitudCapacitacion }>(`${BASE}/${id}`),
  responder: (
    id: number,
    payload: { acepta: boolean; comentario?: string; fecha_sugerida?: string; hora_sugerida?: string }
  ) => api.post<{ info: string }>(`${BASE}/${id}/responder`, payload),
  cancelar: (id: number, motivo?: string) =>
    api.post<{ info: string }>(`${BASE}/${id}/cancelar`, { motivo }),
  datosCierre: (id: number) => api.get<{ datos: DatosCierre }>(`${BASE}/${id}/datos-cierre`),
  cerrar: (id: number, payload: CierrePayload) =>
    api.post<{ info: string }>(`${BASE}/${id}/cerrar`, payload),
};

export default SolicitudCapacitacionAPI;

/** Si el usuario entra a Solicitudes de Capacitacion lo decide api-central:
 * fiscales, supervisores y jefes por cargo, administrativos por el tipo de
 * funcionario del DWH (que la app no conoce), y el capacitador para cerrar.
 * "1" / "0"; sin valor = todavia no se sabe. Se limpia al cerrar sesion.
 * Mismo esquema que el permiso de RRHH y SST. */
export const CAPACITACION_PERMISO_SESSION_KEY = "capacitacion_puede_solicitar_session";

export const permisoCapacitacionEnSesion = (): boolean | null => {
  const v = sessionStorage.getItem(CAPACITACION_PERMISO_SESSION_KEY);
  return v === null ? null : v === "1";
};

let permisoCapacitacionEnCurso: Promise<boolean | null> | null = null;

export const cargarPermisoCapacitacion = (): Promise<boolean | null> => {
  const guardado = permisoCapacitacionEnSesion();
  if (guardado !== null) return Promise.resolve(guardado);
  if (!permisoCapacitacionEnCurso) {
    permisoCapacitacionEnCurso = SolicitudCapacitacionAPI.rol()
      .then(({ data }) => {
        const puede = Boolean(data?.datos?.solicitante || data?.datos?.capacitador);
        sessionStorage.setItem(CAPACITACION_PERMISO_SESSION_KEY, puede ? "1" : "0");
        return puede;
      })
      .catch(() => null) // sin marcar: se reintenta en la proxima navegacion
      .finally(() => {
        permisoCapacitacionEnCurso = null;
      });
  }
  return permisoCapacitacionEnCurso;
};
